import { defineStore } from 'pinia';
import { useApiClient } from '@/app/core/api-client';
import { StoreNames } from '@/app/shared/store-names';
const DEMO_STORAGE_KEY = 'onebeat:demo-user-settings';
type DemoUserSettings = {
  name?: string;
  sheetsUrl?: string;
  sheetsId?: string;
  replenishmentSheetsUrl?: string;
  replenishmentSheetsId?: string;
  allocationsSheetsUrl?: string;
  allocationsSheetsId?: string;
  inventorySheetsUrl?: string;
  inventorySheetsId?: string;
};
function loadDemoSettings(): DemoUserSettings {
  if (typeof window === 'undefined') {
    return {};
  }
  const raw = window.localStorage.getItem(DEMO_STORAGE_KEY);
  if (!raw) {
    return {};
  }
  try {
    return JSON.parse(raw) as DemoUserSettings;
  } catch (error) {
    return {};
  }
}
function saveDemoSettings(settings: DemoUserSettings) {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(settings));
}
const storedDemo = loadDemoSettings();
export const useUserStore = defineStore(StoreNames.User, {
  state: () => ({
    email: '',
    email_verified: false,
    family_name: '',
    given_name: '',
    name: storedDemo.name ?? 'User',
    preferred_username: '',
    id: '',
    demoReplenishmentSheetsUrl: storedDemo.replenishmentSheetsUrl ?? storedDemo.sheetsUrl ?? '',
    demoReplenishmentSheetsId: storedDemo.replenishmentSheetsId ?? storedDemo.sheetsId ?? '',
    demoAllocationsSheetsUrl: storedDemo.allocationsSheetsUrl ?? '',
    demoAllocationsSheetsId: storedDemo.allocationsSheetsId ?? '',
    demoInventorySheetsUrl: storedDemo.inventorySheetsUrl ?? '',
    demoInventorySheetsId: storedDemo.inventorySheetsId ?? '',
  }),
  actions: {
    async fetch(): Promise<void> {
      const api = useApiClient();
      const data = await api.auth.getUserInfo();
      if (!data) {
        return;
      }
      this.email = data.email;
      this.email_verified = data.email_verified;
      this.family_name = data.family_name;
      this.given_name = data.given_name;
      this.name = data.name;
      this.preferred_username = data.preferred_username;
      this.id = data.sub;
    },
    async clear(): Promise<void> {
      this.$reset();
    },
    saveDemoSettings(settings: {
      name: string;
      replenishmentSheetsUrl?: string;
      replenishmentSheetsId?: string;
      allocationsSheetsUrl?: string;
      allocationsSheetsId?: string;
      inventorySheetsUrl?: string;
      inventorySheetsId?: string;
    }): void {
      this.name = settings.name;
      this.demoReplenishmentSheetsUrl =
        settings.replenishmentSheetsUrl ?? this.demoReplenishmentSheetsUrl;
      this.demoReplenishmentSheetsId =
        settings.replenishmentSheetsId ?? this.demoReplenishmentSheetsId;
      this.demoAllocationsSheetsUrl =
        settings.allocationsSheetsUrl ?? this.demoAllocationsSheetsUrl;
      this.demoAllocationsSheetsId = settings.allocationsSheetsId ?? this.demoAllocationsSheetsId;
      this.demoInventorySheetsUrl = settings.inventorySheetsUrl ?? this.demoInventorySheetsUrl;
      this.demoInventorySheetsId = settings.inventorySheetsId ?? this.demoInventorySheetsId;
      saveDemoSettings({
        name: this.name,
        replenishmentSheetsUrl: this.demoReplenishmentSheetsUrl,
        replenishmentSheetsId: this.demoReplenishmentSheetsId,
        allocationsSheetsUrl: this.demoAllocationsSheetsUrl,
        allocationsSheetsId: this.demoAllocationsSheetsId,
        inventorySheetsUrl: this.demoInventorySheetsUrl,
        inventorySheetsId: this.demoInventorySheetsId,
      });
    },
  },
});
